import {
  Paper,
  Typography,
  Stack,
  Box,
  Chip,
  Link as MuiLink,
  Divider,
} from "@mui/material";

const Field = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ mt: 0.5 }}>{children}</Box>
  </Box>
);

const ChannelInfoPanel = ({ channel, sx }) => {
  if (!channel) {
    return (
      <Paper sx={{ p: 3, ...sx }}>
        <Typography variant="subtitle1">Informacion del canal</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Sin datos disponibles.
        </Typography>
      </Paper>
    );
  }

  const srtEndpoints = Array.isArray(channel?.srtEndpoints)
    ? channel.srtEndpoints
    : Array.isArray(channel?.inputs)
    ? channel.inputs
    : [];

  const hlsUrl =
    channel?.hlsUrl ||
    channel?.destinationUrl ||
    channel?.hlsDestinationUrl ||
    null;

  const pipelines = Array.isArray(channel?.pipelines) ? channel.pipelines : [];

  const inputSpec = channel?.inputSpecification || {};

  return (
    <Paper sx={{ p: 3, ...sx }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Informacion del canal
      </Typography>
      <Stack spacing={2} divider={<Divider flexItem />}>
        <Field label="ARN">
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
          >
            {channel?.arn || "-"}
          </Typography>
        </Field>

        <Field label="Class">
          <Chip
            size="small"
            label={channel?.channelClass || "-"}
            variant="outlined"
          />
        </Field>

        <Field label="SRT input endpoints">
          {srtEndpoints.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {srtEndpoints.map((ep, idx) => {
                const value =
                  typeof ep === "string"
                    ? ep
                    : ep?.url || ep?.endpoint || ep?.destination || JSON.stringify(ep);
                return (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                  >
                    {value}
                  </Typography>
                );
              })}
            </Stack>
          )}
        </Field>

        <Field label="HLS destination URL">
          {hlsUrl ? (
            <MuiLink
              href={hlsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: "break-all" }}
            >
              {hlsUrl}
            </MuiLink>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </Field>

        <Field label="Pipelines">
          {pipelines.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {pipelines.map((p, idx) => {
                const pipelineId =
                  p?.pipelineId ?? p?.id ?? idx.toString();
                const activeInput =
                  p?.activeInputAttachmentName ||
                  p?.activeInputName ||
                  p?.activeInput ||
                  "-";
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={`#${pipelineId}: ${activeInput}`}
                    variant="outlined"
                  />
                );
              })}
            </Stack>
          )}
        </Field>

        <Field label="Input specification">
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Codec: ${inputSpec?.codec || "-"}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Resolution: ${inputSpec?.resolution || "-"}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Max bitrate: ${inputSpec?.maximumBitrate || inputSpec?.maxBitrate || "-"}`}
              variant="outlined"
            />
          </Stack>
        </Field>
      </Stack>
    </Paper>
  );
};

export default ChannelInfoPanel;
